import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { DataGridItem } from "components/data-grid";
import { injectIntl } from "react-intl";
import { AssetCategorySelect } from "components/select";
import FormAdvancedField from "components/form/form-advanced-field";
import { NewAssetForm } from "pages/asset/forms/new-form";
import FormFieldError from "components/form/form-field-error";
import { AssetDisplay } from "components/thumbnail";
import AssetSelect from "../../../../../components/select/asset-select";

const LayoutAssetForm = ({
  layout,
  handleChange,
  intl,
  excluded,
  addToExclusions,
  layoutPath,
  customerId,
  isOpen,
  setIsOpen,
  selectedCategoryIds,
  setSelectedCategoryIds,
}) => {
  const handleAssetChange = useCallback(
    event => {
      const value = event.target.value;
      addToExclusions(value, layout.assetId);
      handleChange(`${layoutPath}.assetId`)(value);
      handleChange(`${layoutPath}.assetIdCategoryIds`)(layout.assetIdCategoryIds);
      handleChange(`${layoutPath}.pos`)({ x: 0, y: 0 });
    },
    [addToExclusions, handleChange, layout.assetId, layout.assetIdCategoryIds, layoutPath],
  );

  const handleAssetUpload = useCallback(
    name => ({ base64, size }) => {
      handleChange(name)(base64);
      handleChange(`${layoutPath}.size`)(size);
      handleChange(`${layoutPath}.originalSize`)(size);
      handleChange(`${layoutPath}.pos`)({ x: 0, y: 0 });
    },
    [handleChange, layoutPath],
  );

  const handleOpenNewAssetForm = useCallback(
    isOpen => {
      setIsOpen(isOpen);
      if (isOpen) {
        handleChange(`${layoutPath}.assetId`)("");
        handleChange(`${layoutPath}.assetIdCodename`)("");
        handleChange(`${layoutPath}.format`)("");
        handleChange(`${layoutPath}.assetIdCategoryIds`)(null);
      } else {
        handleChange(`${layoutPath}.asset.codename`)("");
        handleChange(`${layoutPath}.asset.description`)("");
        handleChange(`${layoutPath}.asset.image`)("");
      }
    },
    [handleChange, layoutPath, setIsOpen],
  );

  return (
    <>
      <DataGridItem big>
        <DataGridItem big>
          <DataGridItem>
            <AssetCategorySelect
              disabled={isOpen}
              label={intl.formatMessage({ id: "common.asset.selectCategory" })}
              onChange={setSelectedCategoryIds}
              value={selectedCategoryIds}
              multiple
            />
          </DataGridItem>
          <DataGridItem>
            <AssetSelect
              customerId={customerId}
              name={`${layoutPath}.assetId`}
              label={intl.formatMessage({ id: "common.asset.selectFile" })}
              handleChange={handleAssetChange}
              value={layout.assetId}
              variables={selectedCategoryIds ? { category_ids: selectedCategoryIds } : {}}
              disabled={isOpen || !selectedCategoryIds}
              excluded={excluded}
            />
          </DataGridItem>
        </DataGridItem>
        <DataGridItem big>
          <FormAdvancedField
            onVisibilityChange={handleOpenNewAssetForm}
            label={intl.formatMessage({ id: "app.form.graphicset.asset.new" })}
          >
            <FormFieldError name={`${layoutPath}.asset`} />
            <NewAssetForm
              disabled={!isOpen}
              onChange={handleChange}
              onImageChange={handleAssetUpload}
              values={layout.asset}
              path={`${layoutPath}.asset.`}
              defaultCategories={["OTHER"]}
            />
          </FormAdvancedField>
        </DataGridItem>
      </DataGridItem>
      {layout.assetId && (
        <DataGridItem big>
          <AssetDisplay asset={{ id: layout.assetId, customerId, format: layout.asset.format }} width={150} />
        </DataGridItem>
      )}
    </>
  );
};

LayoutAssetForm.propTypes = {
  /**
   * Función que se invoca al modificar algún valor
   */
  handleChange: PropTypes.func,
  /**
   * Objeto con los valores que se quieren representar
   */
  layout: PropTypes.object,
  /**
   * Función que añade el assetId a las exclusions
   */
  addToExclusions: PropTypes.func,
  /**
   * Valores a excluir del selector
   */
  excluded: PropTypes.array,
  /**
   * Path del layout en los datos
   */
  layoutPath: PropTypes.string,
  /*
   * CustomerId del graphicset
   */
  customerId: PropTypes.string,
  /**
   * Indica si el formulario de nuevo asset está desplegado
   */
  isOpen: PropTypes.bool,
  /**
   * Callback para cambiar el valor "isOpen"
   */
  setIsOpen: PropTypes.func,
  /**
   * Ids de las categorías seleccionadas en el filtrado
   */
  selectedCategoryIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * Callback que cambia las categorías seleccionadas en el filtrado
   */
  setSelectedCategoryIds: PropTypes.func,
};

export default injectIntl(LayoutAssetForm);
